import { useMemo } from "react";
import { useSearchParams } from "react-router";

import { getSearchQuery } from "components/SearchInput/helpers";
import { getFiltersPredicationFromURI, getSortOptionFromURI } from "components/Filters/helpers";
import { SearchQueryOrderDirection } from "types/generated";

import useInitialDefaultView from "./useInitialDefaultView";

type UseFiltersVariables = {
  filterType: string;
  initialSortDirection: SearchQueryOrderDirection;
  initialSortOption: string;
};

const useFiltersVariables = ({
  filterType,
  initialSortDirection,
  initialSortOption,
}: UseFiltersVariables) => {
  const [searchParams] = useSearchParams();

  const defaultView = useInitialDefaultView(filterType);

  const defaultFilterVariables = useMemo(
    () => ({
      searchInput: defaultView.data?.search || "",
      sortOptionFields:
        defaultView.data?.sortOption && defaultView.data?.sortDirection
          ? [{ field: defaultView.data?.sortOption, direction: defaultView.data?.sortDirection }]
          : [{ field: initialSortOption, direction: initialSortDirection }],
      predicates: [...(defaultView.data?.predicates?.values() || [])],
    }),
    [defaultView.data, initialSortDirection, initialSortOption]
  );

  const filterVariables = useMemo(() => {
    const [fallbackSort] = defaultFilterVariables.sortOptionFields;

    return {
      searchInput: getSearchQuery(searchParams),
      sortOptionFields: getSortOptionFromURI(
        searchParams,
        fallbackSort.field,
        fallbackSort.direction
      ),
      predicates: [...(getFiltersPredicationFromURI(searchParams)?.values() || [])],
    };
  }, [searchParams, defaultFilterVariables]);
  if (!searchParams.size) {
    return {
      filterVariables: defaultFilterVariables,
      filterVariablesLoaded: defaultView.loaded,
    };
  }

  return { filterVariables, filterVariablesLoaded: defaultView.loaded };
};

export default useFiltersVariables;
